/**
 * Three things to do today ([Gamification](../../../docs/GAMIFICATION.md#daily-quests)).
 *
 * A reason to open the app on a day a child was not otherwise going to, drawn
 * from the `17-profile` mockup's *Today's quests* card. The rules are all in
 * `packages/core` and the progress is counted by the server, so this screen
 * draws numbers rather than working any of them out
 * ([D-077](../../../docs/DECISIONS.md#d-077)).
 *
 * **There is nothing to tap.** No claim button, no badge waiting to be cleared.
 * A quest a child has finished has already been paid for, at the end of the
 * session that finished it — a reward needing a second tap is a reward some
 * children never collect, and a dot that wants clearing is a nag.
 *
 * **An unfinished quest is never a scold.** No red, no "you missed this", and
 * nothing tomorrow about what yesterday did not do.
 */

import { useEffect, useState } from 'react';
import { type Client, type QuestDay, type Trouble, troubleFrom } from './api.js';
import Sneakers from './Sneakers.js';

type Quest = QuestDay['quests'][number];

/** The child's own date, which is the one the quests are counted in (D-021). */
export function localDay(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${String(now.getFullYear())}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** What Sneakers says about how the day is going. Never about what is left. */
function encouragement(done: number, total: number): string {
  if (total === 0) return 'Nothing to do today — go and play!';
  if (done === 0) return "Here's what we could do today!";
  if (done < total) return 'Nice one. Look at you go!';
  return 'All three! That is a good day.';
}

function Row({ quest, last }: { readonly quest: Quest; readonly last: boolean }) {
  const pct = Math.round((quest.done / quest.target) * 100);
  return (
    <div className="lrow" style={last ? { border: 0 } : undefined}>
      <div className="ic" aria-hidden="true">
        {quest.complete ? '✅' : '⬜'}
      </div>
      <div className="tx" style={{ fontSize: '0.8125rem' }}>
        {quest.says}
        {/*
          A bar only where there is something to fill. "Finish a lesson" is done
          or not done, and a bar that can only ever be empty or full is a worse
          tick box than a tick box.
        */}
        {quest.target > 1 && !quest.complete && (
          <div
            className="bar"
            style={{ height: 9, marginTop: 5 }}
            role="progressbar"
            aria-valuenow={quest.done}
            aria-valuemin={0}
            aria-valuemax={quest.target}
            aria-label={quest.says}
          >
            <i style={{ width: `${String(pct)}%` }} />
          </div>
        )}
      </div>
      {quest.target > 1 && (
        <span className="tiny" style={{ fontWeight: 800 }}>
          {quest.done}/{quest.target}
        </span>
      )}
    </div>
  );
}

export default function Quests({ client }: { readonly client: Client }) {
  const [today, setToday] = useState<QuestDay | null>(null);
  const [trouble, setTrouble] = useState<Trouble | null>(null);

  useEffect(() => {
    let live = true;
    // Through `Promise.resolve` because a server older than this app has no
    // `quest` router and the call *throws* rather than rejecting (D-066), which
    // would take the tab down instead of showing the trouble.
    Promise.resolve()
      .then(() => client.quest.today.query({ day: localDay() }))
      .then((next) => {
        if (live) setToday(next);
      })
      .catch((error: unknown) => {
        if (live) setTrouble(troubleFrom(error));
      });
    return () => {
      live = false;
    };
  }, [client]);

  if (trouble !== null) {
    return (
      <div className="grow pad center">
        <p className="sub" role="alert">
          {trouble.kind === 'offline'
            ? 'No connection. Is the wi-fi on?'
            : "Today's quests would not load."}
        </p>
      </div>
    );
  }

  if (today === null) {
    return (
      <div className="grow pad center">
        <p className="sub">Looking at today…</p>
      </div>
    );
  }

  const done = today.quests.filter((quest) => quest.complete).length;

  return (
    <div className="grow pad" style={{ overflowY: 'auto' }}>
      <Sneakers text={encouragement(done, today.quests.length)} audioRef={null} />

      <div className="card" style={{ marginTop: 12 }}>
        <div className="between">
          <h3>Today&rsquo;s quests</h3>
          <span className="tiny" style={{ fontWeight: 800 }}>
            {done}/{today.quests.length}
          </span>
        </div>
        {today.quests.map((quest, i) => (
          <Row key={quest.kind} quest={quest} last={i === today.quests.length - 1} />
        ))}
      </div>

      <p className="tiny" style={{ marginTop: 12 }}>
        {done === today.quests.length && today.quests.length > 0
          ? 'Finished! New ones tomorrow.'
          : 'New quests every day. Nothing happens if you miss one.'}
      </p>
    </div>
  );
}
